import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from '@emotion/styled';
import { colors } from '@modernhealth/vital-foundation';
import {
    BodyTextMedium,
    BodyTextSmall,
    HeadingText10,
    HeadingText20,
    HeadingText30,
    HeadingText50,
    pxToRem,
} from '@modernhealth/vital-components';

import { CrisisModalTestID } from 'src/modules/benefits/models/testsIds';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import { useGetCrisisProviderCountryAssignmentsQueryQuery } from 'src/modules/user/documents/getCrisisProviderCountryAssignmentsQuery.generated';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { CountrySelect } from 'src/shared/components/CountrySelect';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';
import helpIcon from 'src/shared/svg/icon-help.svg';
import phoneIcon from 'src/shared/svg/icon-phone.svg';

interface StyledIconProps {
    iconUrl: string;
}

const StyledIcon = styled('i')<StyledIconProps>(({ iconUrl }) => ({
    backgroundImage: `url('${iconUrl}')`,
    width: '1.75rem',
    height: '1.75rem',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const ContentWrapper = styled.div({
    maxWidth: 700,
    padding: 10,
    overflowY: 'auto',
    maxHeight: pxToRem(600),
});

const NumberContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: 25,
});

const LabelContainer = styled.div({
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'center',
    marginBottom: 16,
});

const InfoContainer = styled.div({
    width: '100%',
    borderRadius: 5,
    padding: '15px 12px',
    backgroundColor: hexToRgba(colors.gray05, 0.5),
    color: colors.gray65,
    p: {
        lineHeight: 1.8,
    },
});

const InputContainer = styled.div({
    marginTop: pxToRem(20),
});

const urls = {
    internationalSuicideHotlines: 'http://www.suicide.org/international-suicide-hotlines.html',
    internationalHelplines: 'https://findahelpline.com/',
    volunteerPhone: 'https://www.befrienders.org/help-and-support',
};

const AHP_MODAL_COPY =
    'If you are experiencing a mental health emergency, please go to the nearest emergency room or contact a local emergency response line.';

export const CrisisModalContent: React.FC = () => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const countries = useSelector(getSortedCountries);
    const { showCrisisResourceAhpCopy, showSuicideAndCrisisLifeline } = useFlags();
    const [localizedVersion] = useLocalizedVersion();

    const { data: countryCrisisProviders, loading: loadingCountries } =
        useGetCrisisProviderCountryAssignmentsQueryQuery({
            fetchPolicy: 'cache-first',
        });
    const [selectedCountry, setSelectedCountry] = useState(user.country ?? '');

    const filteredCountryAssigments = useMemo(
        () =>
            countryCrisisProviders?.crisisProviderCountryAssignments?.filter(assigment => {
                return assigment?.country.name === selectedCountry;
            }),
        [selectedCountry, countryCrisisProviders?.crisisProviderCountryAssignments],
    );

    const selectedCountryInfo = useMemo(
        () => countries.find(country => country.name === selectedCountry),
        [selectedCountry, countries],
    );

    const policeContact =
        selectedCountryInfo?.policePhoneNumber ??
        t('shared:crisis_modal.police_contact.description');

    const suicideAndCrisisLifeline = selectedCountryInfo?.suicideAndCrisisLifeline;

    const suicideHotline =
        selectedCountryInfo?.suicideHotlineNumber ??
        t('shared:crisis_modal.suicide_hotline.description');

    return (
        <ContentWrapper data-testid={CrisisModalTestID.Modal}>
            <HeadingText50 marginBottom={16}>{t(`shared:crisis_modal.header`)}</HeadingText50>
            <BodyTextMedium marginBottom={36}>
                {showCrisisResourceAhpCopy && !localizedVersion
                    ? AHP_MODAL_COPY
                    : t(`shared:crisis_modal.description`)}
            </BodyTextMedium>
            <InputContainer>
                <CountrySelect
                    id="crisisFormCountryInput"
                    defaultValue={user.country}
                    onChange={country => {
                        if (!country) {
                            return;
                        }
                        setSelectedCountry(country.value);
                    }}
                />
            </InputContainer>

            {!loadingCountries
                ? filteredCountryAssigments?.map(crisisProvider => {
                      return (
                          <NumberContainer key={`${crisisProvider?.crisisProvider.name}`}>
                              <LabelContainer>
                                  <StyledIcon iconUrl={helpIcon} />{' '}
                                  <HeadingText20 marginLeft={16}>
                                      {crisisProvider?.crisisProvider.name}:
                                  </HeadingText20>
                              </LabelContainer>
                              <InfoContainer>
                                  <BodyTextSmall>{crisisProvider?.phoneNumber}</BodyTextSmall>
                              </InfoContainer>
                          </NumberContainer>
                      );
                  })
                : null}

            {showSuicideAndCrisisLifeline &&
                suicideAndCrisisLifeline &&
                selectedCountry === 'United States' && (
                    <NumberContainer>
                        <LabelContainer>
                            <StyledIcon iconUrl={phoneIcon} />
                            <HeadingText20 marginLeft={16}>
                                {t('shared:crisis_modal.suicide_crisis_lifeline.label')}
                            </HeadingText20>
                        </LabelContainer>
                        <InfoContainer>
                            <BodyTextSmall>{suicideAndCrisisLifeline}</BodyTextSmall>
                        </InfoContainer>
                    </NumberContainer>
                )}

            {suicideHotline && selectedCountry !== 'United States' && (
                <NumberContainer>
                    <LabelContainer>
                        <StyledIcon iconUrl={phoneIcon} />
                        <HeadingText20 marginLeft={16}>
                            {t('shared:crisis_modal.suicide_hotline.label')}:
                        </HeadingText20>
                    </LabelContainer>
                    <InfoContainer>
                        <BodyTextSmall>{suicideHotline}</BodyTextSmall>
                    </InfoContainer>
                </NumberContainer>
            )}

            <NumberContainer>
                <LabelContainer>
                    <StyledIcon iconUrl={helpIcon} />
                    <HeadingText20 marginLeft={16}>
                        {t('shared:crisis_modal.police_contact.label')}:
                    </HeadingText20>
                </LabelContainer>
                <InfoContainer>
                    <BodyTextSmall>{policeContact}</BodyTextSmall>
                </InfoContainer>
            </NumberContainer>

            <HeadingText30 marginTop={32}>
                {t('shared:crisis_modal.international.label')}
            </HeadingText30>
            <HeadingText10 underline fontColor="blue55" marginTop={24}>
                <a
                    data-testid={CrisisModalTestID.InternationalSuicideHotlines}
                    target="_blank"
                    href={urls.internationalSuicideHotlines}
                    rel="noopener noreferrer"
                >
                    {t('shared:crisis_modal.international_suicide_hotlines.title')}
                </a>
            </HeadingText10>
            <HeadingText10 underline fontColor="blue55" marginTop={20}>
                <a
                    target="_blank"
                    data-testid={CrisisModalTestID.InternationalHelplines}
                    href={urls.internationalHelplines}
                    rel="noopener noreferrer"
                >
                    {t('shared:crisis_modal.international_helplines.title')}
                </a>
            </HeadingText10>
            <HeadingText10 underline fontColor="blue55" marginTop={20}>
                <a
                    target="_blank"
                    data-testid={CrisisModalTestID.VolunteerPhone}
                    href={urls.volunteerPhone}
                    rel="noopener noreferrer"
                >
                    {t('shared:crisis_modal.volunteer_phone.title')}
                </a>
            </HeadingText10>
        </ContentWrapper>
    );
};
